import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtEur } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Skeleton } from "../components/ui/skeleton";
import { Vault, TrendUp, Coins, Wallet, ArrowDownRight, ArrowUpRight, Receipt, Wrench, Sparkle } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import NewServiceDialog from "./components/NewServiceDialog";
import { HistoryCharts } from "./components/HistoryCharts";
import { PushInvitationBanner } from "../components/PushInvitationBanner";
import OnboardingBanner from "../components/OnboardingBanner";
import NextBestActionHero from "../components/NextBestActionHero";
import TrialExpiredBanner from "../components/TrialExpiredBanner";
import { FEATURES } from "../lib/features";
import { useDashboardTour, resetDashboardTour } from "../hooks/useDashboardTour";
import { PendingReviewSection } from "../components/PendingReviewSection";
import { AEATDeadlinesBanner } from "../components/AEATDeadlinesBanner";
import { InvitarMiGestorCard } from "../components/GestorMultiOrgCards";
import TrialWidget from "../components/TrialWidget";

function Metric({ label, value, sublabel, accent, icon: Icon, testid }) {
  return (
    <div className="metric-card p-5 lg:col-span-3 relative" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{label}</div>
        {Icon && <Icon size={16} weight="duotone" className="text-[var(--ag-text-muted)] shrink-0" />}
      </div>
      <div className={`mt-3 font-display font-bold text-2xl xl:text-3xl tracking-tighter tabular-nums truncate ${accent || ""}`}>{value}</div>
      {sublabel && <div className="mt-2 text-xs text-gray-500 tabular-nums">{sublabel}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { isSolo, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);  // Sprint UX-1 · P0-2E-2 · captura runtime errors
  const [openNew, setOpenNew] = useState(false);
  const [activation, setActivation] = useState(null);  // Fase 2B · from NBA hero

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [s, u] = await Promise.all([
        api.get("/dashboard/stats"),
        api.get("/users"),
      ]);
      setStats(s.data); setUsers(u.data);
    } catch (e) {
      // Sprint UX-1 · P0-2E-2 · evitar runtime overlay React exponiendo stack
      const status = e?.response?.status;
      const detail = status === 429
        ? "Estamos recibiendo demasiadas peticiones. Reintentando en unos segundos…"
        : status === 401 || status === 403
          ? "Sesión expirada. Vuelve a iniciar sesión."
          : "No podemos cargar tu panel ahora mismo.";
      setLoadError({ status, detail });
      // Auto-retry con backoff solo en 429
      if (status === 429) {
        setTimeout(() => load(), 6000);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Mega-Sprint B · Tour guiado para nuevos titulares (1ª vez)
  useDashboardTour({ enabled: !loading && !!stats });

  // Fase 2B · Colapsar KPIs financieros SOLO si activation está cargada Y titular new/empty
  const isNewOrEmpty = activation !== null && (
    !activation.is_activated ||
    (Number(stats?.total_ingresos || 0) === 0 && Number(stats?.services_count || 0) === 0)
  );

  if (loading || !stats) {
    // Sprint UX-1 · P0-2E-2 · si hay error persistente, mostrar UI recuperable en lugar de crash
    if (loadError && !loading) {
      return (
        <div className="max-w-2xl mx-auto py-12 px-4" data-testid="dashboard-load-error">
          <div className="ag-card p-8 text-center border-red-500/30 bg-red-500/5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-red-400">Error temporal</div>
            <h2 className="font-display text-2xl font-black tracking-tighter mt-2">Ups, algo no cargó bien</h2>
            <p className="text-sm text-[var(--ag-text-dim)] mt-3">{loadError.detail}</p>
            <div className="text-[11px] text-[var(--ag-text-dim)] mt-4">
              Código: {loadError.status || "network"}
            </div>
            <Button
              onClick={load}
              className="ag-btn-gold mt-6 h-10 px-6"
              data-testid="dashboard-load-retry"
            >
              Reintentar ahora
            </Button>
          </div>
        </div>
      );
    }
    return <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[...Array(6)].map((_,i)=><Skeleton key={i} className="h-32 rounded-sm" />)}</div>;
  }

  return (
    <div className="space-y-8">
      {/* Fase 2B · NBA Hero primero (contextual · reemplaza KPIs 0€ visualmente) */}
      {user?.role === "admin" && (
        <NextBestActionHero onLoaded={setActivation} />
      )}

      {/* Notification stack · agrupado en lugar de apilado */}
      <div className="space-y-3">
        {user?.role === "admin" && <TrialExpiredBanner />}
        {user?.role === "admin" && <TrialWidget />}
        <AEATDeadlinesBanner />
        {/* Sprint UX-1 · Fase 2E P1-2E-3 · Legacy OnboardingBanner sólo cuando NBA todavía no activado.
            La NBA hero de arriba ya cubre el estado "no activado" con CTAs contextuales, así que
            evitamos duplicar el mensaje "3 de 5 pasos completados" cuando el titular ya está activo. */}
        {activation && !activation.is_activated
          ? <OnboardingBanner role={user?.role} />
          : null}
        <PushInvitationBanner role="user" />
      </div>
      <PendingReviewSection />
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Panel del titular · {stats.month}</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mt-1">Control de Operaciones</h1>
          {isSolo && (
            <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 bg-[var(--ag-aqua)]/15 border border-[var(--ag-aqua)]/30 rounded text-[10px] uppercase tracking-wider text-[var(--ag-aqua-soft)]" data-testid="dashboard-solo-badge">
              <Sparkle size={10} weight="fill" /> Modo Solo Titular
            </div>
          )}
        </div>
        <Button onClick={()=>setOpenNew(true)} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] text-[var(--ag-midnight)] font-semibold rounded-md h-10 px-5" data-testid="new-service-btn">
          <Wrench className="mr-2" weight="duotone" /> Nuevo servicio
        </Button>
        <button
          onClick={() => { resetDashboardTour(); window.location.reload(); }}
          className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-[var(--ag-gold)] underline ml-2"
          data-testid="dashboard-replay-tour"
          title="Volver a ver el tour guiado"
        >Ver tutorial</button>
      </header>

      {stats.unread_availability_count > 0 && (
        <a href="/availability" className="ag-card p-4 border-[var(--ag-gold)]/40 flex items-center gap-3 hover:border-[var(--ag-gold)] transition-colors" data-testid="dashboard-availability-alert">
          <div className="w-10 h-10 bg-[var(--ag-gold)] text-[var(--ag-midnight)] grid place-items-center rounded font-display font-black text-lg tabular-nums">{stats.unread_availability_count}</div>
          <div className="flex-1">
            <div className="font-medium text-sm text-[var(--ag-gold)]">Avisos de disponibilidad del equipo pendientes</div>
            <div className="text-xs text-[var(--ag-text-dim)]">Bloqueos, vacaciones o cambios de horario que tu colaborador acaba de notificar. Pulsa para revisar.</div>
          </div>
          <span className="text-[var(--ag-gold)] text-sm font-medium">Revisar →</span>
        </a>
      )}

      {/* Hero metrics · colapsables para titulares nuevos/vacíos (Fase 2B) */}
      {isNewOrEmpty ? (
        <details className="ag-card p-0 group" data-testid="dashboard-metrics-collapsible">
          <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between select-none">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Métricas del negocio</div>
              <div className="text-sm text-[var(--ag-text)] mt-1">
                Sin operación todavía · aparecerán aquí cuando empieces a facturar
              </div>
            </div>
            <div className="text-xs text-[var(--ag-gold)] group-open:rotate-180 transition-transform">▼ Mostrar</div>
          </summary>
          <div className="px-5 pb-5 pt-2">
            <DashboardMetricsGrid stats={stats} users={users} isSolo={isSolo} />
          </div>
        </details>
      ) : (
        <DashboardMetricsGrid stats={stats} users={users} isSolo={isSolo} />
      )}

      <NewServiceDialog open={openNew} onOpenChange={setOpenNew} onCreated={load} />

      {/* Charts */}
      <section className="space-y-2">
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Histórico y desgloses</div>
        <HistoryCharts />
      </section>

      <AffiliateMiniCard />

      {/* Invitar a mi gestor — sólo admin/owner. Reemplaza la tarjeta que antes vivía en /settings. */}
      {(user?.role === "admin" || user?.role === "owner") && (
        <div className="mt-2">
          <InvitarMiGestorCard />
        </div>
      )}
    </div>
  );
}

/* Fase 2B · KPI grid extracted so it can be wrapped in <details> for empty titulars */
function DashboardMetricsGrid({ stats, users, isSolo }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6" data-testid="dashboard-stats">
      <div className="lg:col-span-4 metric-card p-6 bg-[#FFFBEB] border-[#FCD34D]" data-testid="metric-hucha">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-800">Hucha de Impuestos</div>
          <Vault size={20} weight="duotone" className="text-amber-700" />
        </div>
        <div className="mt-3 font-display font-bold text-5xl tracking-tighter tabular-nums text-amber-900 truncate">{fmtEur(stats.hucha_impuestos)}</div>
        <div className="mt-3 text-xs text-amber-900/70 tabular-nums flex gap-4">
          <span>IVA neto: {fmtEur(stats.iva_neto)}</span>
          <span>Retenciones: {fmtEur(stats.total_retenciones)}</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-amber-700/70 mt-2">No disponible · reservado para Hacienda</div>
      </div>

      <div className="lg:col-span-4 metric-card p-6" data-testid="metric-beneficio">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Beneficio Neto Real</div>
          <TrendUp size={20} weight="duotone" className="text-emerald-500" />
        </div>
        <div className={`mt-3 font-display font-bold text-5xl tracking-tighter tabular-nums truncate ${stats.beneficio_neto_real >= 0 ? "text-emerald-500" : "text-red-500"}`}>
          {fmtEur(stats.beneficio_neto_real)}
        </div>
        <div className="mt-3 text-xs text-gray-500 tabular-nums">
          Tras gastos fijos prorrateados: {fmtEur(stats.fixed_costs_prorated)} <span className="text-[var(--ag-text-muted)]">/ {fmtEur(stats.fixed_monthly_costs)} mensuales</span> · Gastos base: {fmtEur(stats.total_gastos_base)}
        </div>
      </div>

      <div className="lg:col-span-4 metric-card p-6" data-testid="metric-ingresos">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Ingresos del mes (IVA incl.)</div>
          <Wallet size={20} weight="duotone" className="text-[var(--ag-gold)]" />
        </div>
        <div className="mt-3 font-display font-bold text-5xl tracking-tighter tabular-nums text-[var(--ag-gold)] truncate">{fmtEur(stats.total_ingresos)}</div>
        <div className="mt-3 text-xs text-gray-500 tabular-nums">
          {stats.services_count} servicios · Cuota titular: {fmtEur(stats.titular_share_total)}
        </div>
      </div>

      <Metric testid="metric-iva-rep" label="IVA Repercutido" value={fmtEur(stats.total_iva_repercutido)} icon={ArrowUpRight} />
      <Metric testid="metric-iva-sop" label="IVA Soportado" value={fmtEur(stats.total_iva_soportado)} icon={ArrowDownRight} />
      <Metric testid="metric-retenciones" label="Retenciones IRPF" value={fmtEur(stats.total_retenciones)} icon={Coins} />
      {!isSolo && (
        <Metric testid="metric-colab" label="A pagar a colaboradores" value={fmtEur(stats.colaborador_neto_total)} icon={Receipt} />
      )}
      <div className="metric-card p-6 lg:col-span-4" data-testid="metric-services-state">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Estado de servicios</div>
        <div className="mt-3 grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500">Pagados</div>
            <div className="font-display font-bold text-3xl tabular-nums text-emerald-600">{stats.services_pagados}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Pendientes</div>
            <div className="font-display font-bold text-3xl tabular-nums text-amber-600">{stats.services_pendientes}</div>
          </div>
        </div>
      </div>
      <div className="metric-card p-6 lg:col-span-4" data-testid="metric-equipo">
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Equipo</div>
        <div className="mt-3 space-y-1">
          {users.slice(0, 4).map((u) => (
            <div key={u.id} className="flex items-center justify-between text-sm">
              <span className="truncate">{u.name}</span>
              <span className="text-xs text-gray-500 uppercase tracking-wider">{u.role}</span>
            </div>
          ))}
          {users.length === 0 && <div className="text-xs text-gray-500">Sin colaboradores</div>}
        </div>
      </div>
    </div>
  );
}

// =======================================================================
// Sprint S-7 · Mi afiliación (mini-card en Dashboard)
// Oculto por defecto en el lanzamiento — re-activar con REACT_APP_FEATURE_AFFILIATES_B2C=true
// =======================================================================
function AffiliateMiniCard() {
  if (!FEATURES.AFFILIATES_B2C) return null;
  return <AffiliateMiniCardInner />;
}

function AffiliateMiniCardInner() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    api.get("/me/affiliate").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) return null;
  const referralUrl = `${window.location.origin}${data.referral_link}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(referralUrl); setCopied(true); setTimeout(()=>setCopied(false), 1500); }
    catch { /* ignore */ }
  };
  const fmt = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);
  return (
    <section className="metric-card p-6 bg-gradient-to-br from-[var(--ag-gold)]/10 to-transparent border border-[var(--ag-gold)]/30" data-testid="dashboard-affiliate">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">Programa de afiliados</div>
          <h3 className="font-display font-black text-xl tracking-tighter mt-1">Gana el 20 % MRR durante 12 meses</h3>
          <p className="text-xs text-gray-500 mt-1">Comparte tu enlace y recibe comisiones cada vez que un nuevo centro se da de alta y paga.</p>
        </div>
        <div className="flex flex-col items-end text-right gap-1">
          <span className="text-[10px] uppercase tracking-wider text-gray-500">Pendiente</span>
          <span className="font-display font-bold text-2xl text-[var(--ag-gold)] tabular-nums" data-testid="aff-mini-pending">{fmt(data.commissions.pending_eur)}</span>
          <span className="text-[10px] text-gray-500">Pagado: {fmt(data.commissions.paid_eur)}</span>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <code className="px-3 py-2 bg-white border border-amber-200 rounded-sm text-xs font-mono flex-1 min-w-[200px] truncate" data-testid="aff-mini-link">{referralUrl}</code>
        <button onClick={copy} className="ag-btn-gold h-9 px-4 text-xs uppercase tracking-wider font-bold" data-testid="aff-mini-copy">
          {copied ? "Copiado ✓" : "Copiar enlace"}
        </button>
        <span className="text-xs text-gray-500" data-testid="aff-mini-refs">{data.referred_orgs?.length || 0} centros referidos</span>
      </div>
    </section>
  );
}
